import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Upload, CheckCircle2, AlertTriangle, FileText, Globe, ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import axios from "axios";
import Logo from "../components/Logo";

export default function PublicProofUpload() {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState("");
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    async function fetchInvoice() {
      try {
        const res = await axios.get(`/api/portal/proof-invoice/${token}`);
        setInvoice(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Dieser Upload-Link ist ungültig oder abgelaufen.");
      } finally {
        setLoading(false);
      }
    }
    if (token) {
      fetchInvoice();
    }
  }, [token]);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > 10 * 1024 * 1024) {
      alert("Die Datei darf maximal 10 MB gross sein.");
      return;
    }

    setFile(selected);
    const reader = new FileReader();
    reader.onload = () => {
      setFileData(reader.result);
    };
    reader.readAsDataURL(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !fileData) {
      alert("Bitte wählen Sie zuerst eine Belegdatei aus (PDF, JPG oder PNG).");
      return;
    }

    setUploading(true);
    try {
      const payload = {
        token,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type || "application/octet-stream",
        fileData,
        notes
      };

      const res = await axios.post("/api/portal/upload-proof", payload);
      setSuccessMessage(res.data.message || "Ihr Beleg wurde erfolgreich hochgeladen und wird geprüft.");
    } catch (err) {
      alert("Upload fehlgeschlagen: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
        <Loader2 className="animate-spin text-[#E63946]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-200">
          <AlertTriangle className="text-red-500 mx-auto mb-3" size={44} />
          <h2 className="text-xl font-bold text-[#0f172a]">Ungültiger Upload-Link</h2>
          <p className="text-sm text-slate-500 mt-2 mb-6">{error}</p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0f172a] text-white font-bold text-xs hover:bg-[#1e293b]"
          >
            Zur Startseite
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between p-4 md:p-8">
      <div className="max-w-2xl w-full mx-auto space-y-6 pt-4">
        {/* Header Branding */}
        <div className="text-center space-y-2">
          <div className="inline-block">
            <Logo size="lg" />
          </div>
          <h1 className="text-2xl font-black text-[#0f172a] mt-2">
            Sicherer Zahlungsbeleg-Upload
          </h1>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            Haben Sie Ihre Rechnung bereits beglichen? Laden Sie hier Ihren Beleg hoch, um eine Sperrung abzuwenden oder Ihr Konto sofort freizuschalten.
          </p>
        </div>

        {/* Invoice Summary Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Rechnungs-Details</span>
              <h2 className="font-bold text-[#0f172a] text-base">{invoice?.invoiceNumber}</h2>
            </div>
            <div className="text-right">
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Offener Betrag</span>
              <div className="text-xl font-black text-[#E63946] font-mono">
                {invoice?.amountFormatted ? (invoice.amountFormatted.startsWith("CHF") ? invoice.amountFormatted : `CHF ${invoice.amountFormatted}`) : `CHF ${(invoice?.amount || 0).toFixed(2)}`}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-slate-400 block">Kunde:</span>
              <span className="font-semibold text-slate-800">{invoice?.customerName}</span>
            </div>
            <div>
              <span className="text-slate-400 block">Domain / Service:</span>
              <span className="font-semibold text-slate-800 flex items-center gap-1.5">
                <Globe size={13} className="text-slate-400" />
                {invoice?.domain}
              </span>
            </div>
            <div>
              <span className="text-slate-400 block">Fälligkeitsdatum:</span>
              <span className="font-mono text-slate-800 font-medium">{invoice?.dueDate || "Fällig"}</span>
            </div>
            <div>
              <span className="text-slate-400 block">Konto-Status:</span>
              <span className={`font-bold ${invoice?.status === "suspended" ? "text-red-600" : "text-emerald-600"}`}>
                {invoice?.status === "suspended" ? "Vorübergehend gesperrt" : "Aktiv"}
              </span>
            </div>
          </div>
        </div>

        {/* Upload Form or Success State */}
        {successMessage ? (
          <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-8 text-center space-y-4">
            <CheckCircle2 className="text-emerald-500 mx-auto" size={50} />
            <h3 className="text-xl font-bold text-slate-900">Zahlungsbeleg eingereicht!</h3>
            <p className="text-sm text-slate-600 max-w-md mx-auto">{successMessage}</p>
            <div className="p-4 rounded-xl bg-emerald-50 text-emerald-800 text-xs font-medium max-w-md mx-auto">
              Unser Buchhaltungsteam prüft den Eingang schnellstmöglich. Nach Verifikation wird Ihr Service automatisch reaktiviert.
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-2">
                Zahlungsbeleg auswählen (PDF, JPG, PNG – max. 10 MB) *
              </label>

              <div className="border-2 border-dashed border-slate-200 hover:border-[#E63946] rounded-xl p-8 text-center transition bg-slate-50/50 cursor-pointer relative">
                <input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  onChange={handleFileChange}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                <Upload size={36} className="mx-auto text-slate-400 mb-2" />
                {file ? (
                  <div>
                    <div className="font-bold text-sm text-[#0f172a]">{file.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                ) : (
                  <div>
                    <div className="font-bold text-sm text-slate-700">Klicken oder Datei hierher ziehen</div>
                    <div className="text-xs text-slate-400 mt-1">Akzeptierte Formate: PDF, PNG, JPG (max. 10 MB)</div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Zusätzliche Mitteilung oder Referenznummer (Optional)
              </label>
              <textarea
                rows="2"
                placeholder="z.B. Überwiesen am 24.10. via PostFinance E-Banking / TWINT Referenz"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-[#E63946]"
              />
            </div>

            <button
              type="submit"
              disabled={uploading || !file}
              className="w-full py-3 rounded-xl bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 text-white font-bold text-sm shadow-md transition flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Beleg wird sicher übertragen...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Zahlungsbeleg jetzt einreichen
                </>
              )}
            </button>
          </form>
        )}

        <div className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5 pt-4">
          <ShieldCheck size={14} className="text-emerald-600" />
          Gesicherte Übertragung mit 256-Bit SSL-Verschlüsselung · RedWORK Switzerland
        </div>
      </div>

      <footer className="text-center text-[11px] text-slate-400 py-6">
        © {new Date().getFullYear()} RedWORK Schweiz · Billing & Host Infrastructure
      </footer>
    </div>
  );
}
